import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, KeyRound, X, Plus } from "lucide-react";

export function NewEmployeeDialog({ open, onOpenChange, onCreated }) {
  const [specs, setSpecs] = useState({});
  const [menuCatalog, setMenuCatalog] = useState({ all: {}, toggleable: [], always_on: [] });
  const [f, setF] = useState({ name: "", email: "", phone: "", specialization: "", password: "", allowed: [], menus: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      api.get("/catalog/specializations").then(r => setSpecs(r.data));
      api.get("/catalog/menus").then(r => setMenuCatalog(r.data)).catch(() => {});
    }
  }, [open]);
  const specKeys = useMemo(() => Object.keys(specs), [specs]);

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.specialization || !f.password) {
      toast.error("Fill name, email, role and password"); return;
    }
    if (f.password.length < 6) { toast.error("Password ≥ 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/employees", {
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
        specialization: f.specialization, password: f.password,
        allowed_specializations: f.allowed.length ? f.allowed : [f.specialization],
        allowed_menus: f.menus,
      });
      toast.success("Employee created", { description: `${f.email} · temporary password shared` });
      setF({ name: "", email: "", phone: "", specialization: "", password: "", allowed: [], menus: [] });
      onOpenChange(false); onCreated?.();
    } catch (e) { toast.error("Create failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const toggleAllowed = (k) => setF(x => ({ ...x, allowed: x.allowed.includes(k) ? x.allowed.filter(a => a !== k) : [...x.allowed, k] }));
  const toggleMenu = (k) => setF(x => ({ ...x, menus: x.menus.includes(k) ? x.menus.filter(a => a !== k) : [...x.menus, k] }));
  const gen = () => setF(x => ({ ...x, password: `Emp@${Math.random().toString(36).slice(2, 8)}` }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl">New Employee</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Creates the employee record + a login account. They'll be asked to change the password on first login.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Full name</Label><Input data-testid="new-emp-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Email (login id)</Label><Input data-testid="new-emp-email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
          <div><Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Phone</Label><Input data-testid="new-emp-phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Primary role</Label>
            <Select value={f.specialization} onValueChange={v => setF({ ...f, specialization: v })}>
              <SelectTrigger data-testid="new-emp-spec"><SelectValue placeholder="Pick a role" /></SelectTrigger>
              <SelectContent>{specKeys.map(k => <SelectItem key={k} value={k}>{specs[k]?.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <div className="flex items-end justify-between">
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Temporary password</Label>
              <button onClick={gen} className="text-[10px] tracking-widest uppercase font-semibold text-zinc-500 hover:text-zinc-900">Generate</button>
            </div>
            <Input data-testid="new-emp-password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} placeholder="At least 6 characters" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Dashboard access · task categories this employee can handle</Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5 border border-zinc-200 p-2 bg-zinc-50 max-h-40 overflow-y-auto">
              {specKeys.map(k => (
                <label key={k} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-white cursor-pointer">
                  <input type="checkbox" data-testid={`new-emp-allowed-${k}`} checked={f.allowed.includes(k)} onChange={() => toggleAllowed(k)} />
                  <span>{specs[k]?.title}</span>
                </label>
              ))}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Leave empty to grant only their primary role. Auto-assignment picks from these categories.</div>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Dashboard menus this employee will see</Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5 border border-zinc-200 p-2 bg-zinc-50">
              {(menuCatalog.toggleable || []).map(k => (
                <label key={k} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-white cursor-pointer">
                  <input type="checkbox" data-testid={`new-emp-menu-${k}`} checked={f.menus.includes(k)} onChange={() => toggleMenu(k)} />
                  <span>{menuCatalog.all?.[k]}</span>
                </label>
              ))}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Overview, Daily Tasks and My Profile are always shown. Tick extras to add Invoices, Clients, Leads or Reports.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-emp-submit" disabled={busy} onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            <UserPlus size={14} className="mr-1.5" /> Create employee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ open, onOpenChange, employee }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPw(""); }, [open]);
  const submit = async () => {
    if (pw.length < 6) { toast.error("Password ≥ 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${employee.id}/password`, { new_password: pw });
      toast.success("Password reset", { description: `Share new password with ${employee.name}` });
      onOpenChange(false);
    } catch (e) { toast.error("Reset failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl">Reset password</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Set a new temporary password for <b>{employee?.name}</b>. They will be asked to change it at next login.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">New password</Label>
          <Input data-testid="reset-pw-input" type="text" value={pw} onChange={e => setPw(e.target.value)} placeholder="≥ 6 characters" />
          <button onClick={() => setPw(`Emp@${Math.random().toString(36).slice(2,8)}`)} className="text-[10px] tracking-widest uppercase font-semibold text-zinc-500 hover:text-zinc-900 mt-1">Generate</button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="reset-pw-submit" disabled={busy} onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            <KeyRound size={14} className="mr-1.5" /> Save new password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
